import { useMemo } from 'react';
import type { ItemV2 } from '@/common/api/types/items';
import type { IVisualizationHeader } from '@/common/api/types/loopVisualization';

interface UseVisualizationLabelMapParams {
  headerForColorField: IVisualizationHeader | null;
  currentColorByField: string;
  uniqueValues: string[];
  itemsForIdLookup: ItemV2[];
  noValueLabel: string;
}

export function useVisualizationLabelMap({
  headerForColorField,
  currentColorByField,
  uniqueValues,
  itemsForIdLookup,
  noValueLabel,
}: UseVisualizationLabelMapParams): Record<string, string> {
  return useMemo(() => {
    const map: Record<string, string> = {};
    const itemNameById = new Map(
      itemsForIdLookup.map((item) => [item.id.toString(), item.name] as const),
    );

    if (!headerForColorField) {
      if (currentColorByField === 'parentItem') {
        map.null = noValueLabel;
        uniqueValues.forEach((parentId) => {
          const parentName = itemNameById.get(parentId);
          if (parentName) {
            map[parentId] = parentName;
          }
        });
      }
      return map;
    }

    if (
      headerForColorField.type === 'CustomPropertySelectorByID' ||
      headerForColorField.type === 'ParentItemSelector'
    ) {
      map.null = noValueLabel;
    }

    if (headerForColorField.type === 'ParentItemSelector' && 'options' in headerForColorField) {
      const neededIds = new Set(uniqueValues);
      const foundIds = new Set<string>();

      (headerForColorField.options as Array<{ id: number | string; name: string }>).forEach(
        (option) => {
          const optionId = option.id.toString();
          if (neededIds.has(optionId)) {
            foundIds.add(optionId);
            map[optionId] = option.name;
          }
        },
      );

      const missingIds = Array.from(neededIds).filter((id) => !foundIds.has(id));
      if (missingIds.length > 0) {
        missingIds.forEach((missingId) => {
          const parentName = itemNameById.get(missingId);
          if (parentName) {
            map[missingId] = parentName;
          }
        });
      }
    } else if ('options' in headerForColorField) {
      (
        headerForColorField.options as Array<{
          id: number | string;
          name?: string;
          label?: string;
        }>
      ).forEach((option) => {
        const displayName = option.name ?? option.label ?? option.id.toString();
        map[option.id.toString()] = displayName;
      });
    }

    return map;
  }, [headerForColorField, currentColorByField, uniqueValues, itemsForIdLookup, noValueLabel]);
}
